/**
 * @description Component-TradeFormTradeTypeTabs
 */
import { useChainId } from '@/hooks/web3/useChain';
import './index.less';

import { TRADE_OVERLAY_TYPE, TRADE_TYPE } from '@/constants/trade';
import { useCurrentPairFromUrl, useGetDefaultLimitPrice, usePairDisabledForLimit } from '@/features/pair/hook';
import { setTradeFormType } from '@/features/trade/actions';
import { useTradeSide, useTradeType } from '@/features/trade/hooks';
import { useAppDispatch } from '@/hooks';
import { formatNumber } from '@/utils/numberUtil';
import classNames from 'classnames';
import { FC, useCallback, useEffect } from 'react';

import { Button } from '@/components/Button';
import SettingsIcon from '@/components/CardWrapper/assets/icon_setting_linear.svg?react';
import { useBackendChainConfig } from '@/features/config/hook';
import { useTradeStore } from '@/features/trade/store';
import { Tabs, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import LimitIcon from './assets/icon_limit.svg?react';
import MarketIcon from './assets/icon_market.svg?react';
import ScaleIcon from './assets/icon_scaled.svg?react';

interface IPropTypes {
  children?: React.ReactNode;
  className?: string;
}
const TradeFormTradeTypeTabs: FC<IPropTypes> = function ({}) {
  const chainId = useChainId();
  const tradeType = useTradeType(chainId);
  const currentPair = useCurrentPairFromUrl(chainId);
  const dispatch = useAppDispatch();
  const tradeSide = useTradeSide(chainId);
  const getDefaultLimitPrice = useGetDefaultLimitPrice(currentPair, tradeSide);
  const { t } = useTranslation();
  const dappConfig = useBackendChainConfig(chainId);
  const { inDisabledPairs, pairDisabledInLimit } = usePairDisabledForLimit(currentPair);
  const {
    setShowTradeAreaOverlay,
    setTradeSize,
    setTradeQuoteSize,
    setTradePercent,
    setLimitPrice,
    setAlignedLimitPrice,
  } = useTradeStore();
  const { toLimitTab, setToLimitTab } = useTradeStore();

  const onTabChange = useCallback(
    async (key: string) => {
      const tradeType = key as TRADE_TYPE;
      setTradeSize('');
      setTradePercent(0);
      setTradeQuoteSize('');
      if ([TRADE_TYPE.LIMIT, TRADE_TYPE.SCALE_LIMIT].includes(tradeType)) {
      }

      chainId &&
        dispatch(
          setTradeFormType({
            chainId,
            tradeType: key as TRADE_TYPE,
          }),
        );
      if (tradeType === TRADE_TYPE.LIMIT && !toLimitTab) {
        const defaultLimitPrice = getDefaultLimitPrice();

        if (defaultLimitPrice) {
          const inputAmountStr = formatNumber(
            defaultLimitPrice.adjustPrice.stringValue,
            currentPair?.priceDecimal,
            false,
          );
          setLimitPrice(inputAmountStr);
          setAlignedLimitPrice(inputAmountStr);
        }
      }
    },
    [
      chainId,
      currentPair?.priceDecimal,
      dispatch,
      getDefaultLimitPrice,
      setTradePercent,
      setTradeQuoteSize,
      toLimitTab,
      setTradeSize,
      setLimitPrice,
      setAlignedLimitPrice,
    ],
  );
  const tabList = [
    {
      key: TRADE_TYPE.MARKET.toString(),
      label: (
        <div className={classNames('syn-trade-form-tab')}>
          <MarketIcon />
          <span>{t('common.tradePage.market')}</span>
        </div>
      ),
    },
    {
      key: TRADE_TYPE.LIMIT.toString(),
      label: dappConfig?.trade?.isDisableLimitOrder ? (
        <Tooltip title="Coming Soon">
          <LimitIcon />
          <span>{t('common.limit')}</span>
        </Tooltip>
      ) : (
        <Tooltip title={inDisabledPairs ? t('common.tradePage.limitDisabled') : ''}>
          <div className={classNames('syn-trade-form-tab', { disabled: pairDisabledInLimit })}>
            <LimitIcon />
            <span>{t('common.limit')}</span>
            {/* <Tooltip title={inDisabledPairs ? '' : t('tooltip.tradePage.feeRebate')}>
              <LabelIcon className="syn-trade-form-tab-label" />
            </Tooltip> */}
          </div>
        </Tooltip>
      ),
      disabled: pairDisabledInLimit,
    },
    {
      key: TRADE_TYPE.SCALE_LIMIT.toString(),
      disabled: pairDisabledInLimit,
      label: dappConfig?.trade?.isDisableLimitOrder ? (
        <Tooltip title="Coming Soon">
          <LimitIcon />
          <span>{t('common.limit')}</span>
        </Tooltip>
      ) : (
        <Tooltip title={inDisabledPairs ? t('common.tradePage.limitDisabled') : ''}>
          <div className={classNames('syn-trade-form-tab', { disabled: pairDisabledInLimit })}>
            <ScaleIcon />
            <span>{t('common.tradePage.scaleLimit')}</span>
            {/* <Tooltip title={inDisabledPairs ? '' : t('tooltip.tradePage.feeRebate')}>
              <LabelIcon className="syn-trade-form-tab-label" />
            </Tooltip> */}
          </div>
        </Tooltip>
      ),
    },
  ];

  useEffect(() => {
    if (toLimitTab) {
      onTabChange(TRADE_TYPE.LIMIT);
      setToLimitTab(false);
    }
    // only depend on this
  }, [toLimitTab]);
  useEffect(() => {
    if (pairDisabledInLimit) {
      onTabChange(TRADE_TYPE.MARKET);
    }
  }, [pairDisabledInLimit]);

  return (
    <div className="syn-trade-form-trade-type-tabs-container">
      <Tabs
        className="syn-trade-form-trade-type-tabs"
        // showSegment={true}
        onChange={onTabChange}
        activeKey={tradeType.toString() || ''}
        items={tabList}
        tabBarExtraContent={
          <Button
            className="syn-trade-form-trade-type-tabs-setting"
            type="text"
            icon={<SettingsIcon />}
            onClick={() => {
              setShowTradeAreaOverlay(TRADE_OVERLAY_TYPE.SETTING);
            }}
            size="small"
          />
        }
      />
    </div>
  );
};

export default TradeFormTradeTypeTabs;
